import { OfflineStateIndicator } from "@/components/app/OfflineStateIndicator"
import { ProjectsMap } from "@/components/project/ProjectsMap"
import { useProjectsList } from "@/hooks/useProjectsList"
import { useNetwork } from "@/hooks/useNetwork"
import { Button } from "@fieldbook/ui/button"
import { ButtonLink } from "@fieldbook/ui/button-link"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@fieldbook/ui/card"
import { Input } from "@fieldbook/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@fieldbook/ui/tabs"
import { cn } from "@fieldbook/ui/utils"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useDebounce } from "@uidotdev/usehooks"
import { ChevronRight, CogIcon, RefreshCwIcon, XIcon } from "lucide-react"
import { useState } from "react"

export const Route = createFileRoute("/_private/projects/")({
  component: ProjectsList,
})

function ProjectsList() {
  const navigate = useNavigate({ from: "/projects" })
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const { data: projects, hasSynced } = useProjectsList({
    searchTerm: debouncedSearchTerm,
  })
  const displayedProjects = projects ?? []
  const { isOnline } = useNetwork()

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden pb-[var(--sai-bottom)] pt-[var(--sai-top)]">
      <div className="flex shrink-0 items-center justify-between align-middle">
        <div className="p-2 text-2xl">Projects</div>
        <div className="flex items-center justify-end gap-2 py-2 pr-2">
          <OfflineStateIndicator />
          <ButtonLink
            to="/settings"
            // @ts-expect-error - allows settings to show in sidebar sheet without changing url
            mask={{ to: window.location.pathname }}
            className="bg-secondary-background text-primary h-9 w-9 rounded-xl p-2"
            size={"icon"}
          >
            <CogIcon />
          </ButtonLink>
        </div>
      </div>
      {!hasSynced && isOnline && (
        <div className="flex w-full shrink-0 justify-end p-2">
          <RefreshCwIcon
            aria-label={"Syncing projects"}
            className={cn("h-5 w-5 animate-spin")}
          />
        </div>
      )}
      {!hasSynced &&
        !isOnline &&
        displayedProjects.length === 0 &&
        !searchTerm && (
          <div role="status" className="shrink-0 p-4 text-sm">
            <p>Connect to the internet to download your projects.</p>
            <p className="text-muted-foreground mt-1">
              This version needs to sync once before you can work offline.
              Downloading will start automatically when you reconnect.
            </p>
          </div>
        )}
      <Tabs defaultValue="list" className="min-h-0 flex-1">
        <TabsList className="bg-secondary-background w-full shrink-0">
          <TabsTrigger className="w-1/2" value="list">
            List
          </TabsTrigger>
          <TabsTrigger className="w-1/2" value="map">
            Map
          </TabsTrigger>
        </TabsList>
        <div className="relative flex w-full shrink-0">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search projects"
            className="outline outline-gray-300 focus:outline-none dark:outline-none"
          />
          {searchTerm !== "" && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
              onClick={() => {
                setSearchTerm("")
              }}
            >
              <XIcon className="size-4" />
              <span className="sr-only">Clear</span>
            </Button>
          )}
        </div>

        <TabsContent value="list" className="min-h-0 overflow-y-auto">
          {displayedProjects.map((project) => {
            const hasDate = project.start_date || project.end_date
            return (
              <Card
                className="flex flex-row rounded-none bg-inherit p-0"
                key={project.id}
                onClick={() =>
                  navigate({ to: "/projects/$id", params: { id: project.id } })
                }
              >
                <div className="flex grow flex-col">
                  <CardHeader className="p-3">
                    <CardTitle>{project.name}</CardTitle>
                    <div className="font-mono text-sm">{project.code}</div>
                    <CardDescription>{project.location_name}</CardDescription>
                  </CardHeader>
                  <CardContent className="px-3 pb-3">
                    {project.client && (
                      <span className="text-muted-foreground">
                        {project.client.name}
                      </span>
                    )}
                    {hasDate && (
                      <>
                        {project.start_date &&
                          new Date(
                            project.start_date,
                          ).toLocaleDateString()}{" "}
                        -{" "}
                        {project.end_date &&
                          new Date(project.end_date).toLocaleDateString()}
                      </>
                    )}
                  </CardContent>
                </div>
                <div className="flex shrink flex-col justify-center pr-2">
                  <ChevronRight height={45} width={45} />
                </div>
              </Card>
            )
          })}
        </TabsContent>
        <TabsContent value="map" className="min-h-0 overflow-hidden">
          <ProjectsMap projects={displayedProjects} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
